/**
 * Data layer for `cohort-analysis`. Thin typed fetch wrappers over /api/v1/cohort-analysis.
 * Raw `fetch()` is confined to this file per the dash contract.
 *
 * Endpoints mirrored (see apps/api/src/routes/cohort-analysis.ts):
 *   POST   /api/v1/cohort-analysis/cohorts
 *   GET    /api/v1/cohort-analysis/cohorts
 *   GET    /api/v1/cohort-analysis/cohorts/:id
 *   PUT    /api/v1/cohort-analysis/cohorts/:id/members
 *   POST   /api/v1/cohort-analysis/cohorts/:id/events
 *   GET    /api/v1/cohort-analysis/cohorts/:id/matrix
 */

export type CohortType = "signup" | "first_conversion" | "acquisition_source";
export type PeriodUnit = "day" | "week" | "month";

export interface CohortRow {
	id: string;
	tenant_id?: string;
	name: string;
	cohort_type: CohortType;
	period_unit: PeriodUnit;
	period_count: number;
	anchor_ts: number;
	created_at?: number;
	updated_at?: number;
}

export interface ListCohortsResponse {
	cohorts: CohortRow[];
}

export interface GetCohortResponse {
	cohort: CohortRow;
}

export interface CreateCohortBody {
	name: string;
	cohort_type: CohortType;
	period_unit: PeriodUnit;
	period_count: number;
	anchor_ts?: number;
}

export interface CreateCohortResponse {
	id: string;
	name: string;
	cohort_type: CohortType;
	period_unit: PeriodUnit;
	period_count: number;
	anchor_ts: number;
}

export interface UpsertMemberBody {
	player_id: string;
	acquired_ts: number;
	acquisition_source?: string | null;
}

export interface UpsertMemberResponse {
	id: string;
	cohort_id: string;
	player_id: string;
	acquired_ts: number;
	acquisition_source: string | null;
}

export interface CreateEventBody {
	member_id: string;
	event_ts: number;
	revenue_cents?: number;
}

export interface CreateEventResponse {
	id: string;
	member_id: string;
	event_ts: number;
	revenue_cents: number;
}

export interface MatrixRow {
	cohort_key?: string;
	size?: number;
	cells?: Array<{
		period_index: number;
		retained_users: number;
		revenue_cents: number;
	}>;
	[key: string]: unknown;
}

export interface MatrixResponse {
	cohort_id: string;
	cohort_type: CohortType;
	period_unit: PeriodUnit;
	period_count: number;
	rows: MatrixRow[];
	totals: {
		members: number;
		retained_users: number;
		revenue_cents: number;
	};
}

const BASE = "/api/v1/cohort-analysis";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function listCohorts(): Promise<ListCohortsResponse> {
	const res = await fetch(`${BASE}/cohorts`, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListCohortsResponse>(res);
}

export async function getCohort(id: string): Promise<GetCohortResponse> {
	const res = await fetch(`${BASE}/cohorts/${encodeURIComponent(id)}`, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<GetCohortResponse>(res);
}

export async function createCohort(
	body: CreateCohortBody,
): Promise<CreateCohortResponse> {
	const res = await fetch(`${BASE}/cohorts`, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<CreateCohortResponse>(res);
}

export async function upsertMember(
	cohortId: string,
	body: UpsertMemberBody,
): Promise<UpsertMemberResponse> {
	const res = await fetch(
		`${BASE}/cohorts/${encodeURIComponent(cohortId)}/members`,
		{
			method: "PUT",
			credentials: "include",
			headers: {
				accept: "application/json",
				"content-type": "application/json",
			},
			body: JSON.stringify(body),
		},
	);
	return parseOrThrow<UpsertMemberResponse>(res);
}

export async function createEvent(
	cohortId: string,
	body: CreateEventBody,
): Promise<CreateEventResponse> {
	const res = await fetch(
		`${BASE}/cohorts/${encodeURIComponent(cohortId)}/events`,
		{
			method: "POST",
			credentials: "include",
			headers: {
				accept: "application/json",
				"content-type": "application/json",
			},
			body: JSON.stringify(body),
		},
	);
	return parseOrThrow<CreateEventResponse>(res);
}

export async function getMatrix(cohortId: string): Promise<MatrixResponse> {
	const res = await fetch(
		`${BASE}/cohorts/${encodeURIComponent(cohortId)}/matrix`,
		{
			method: "GET",
			credentials: "include",
			headers: { accept: "application/json" },
		},
	);
	return parseOrThrow<MatrixResponse>(res);
}

export const cohortAnalysisQueryKeys = {
	all: ["cohort-analysis"] as const,
	list: () => [...cohortAnalysisQueryKeys.all, "list"] as const,
	detail: (id: string) => [...cohortAnalysisQueryKeys.all, "detail", id] as const,
	matrix: (id: string) => [...cohortAnalysisQueryKeys.all, "matrix", id] as const,
};
